/**
 * Játék közbeni HUD (Canvas 2D). A hívó a nézet téglalapjára vág, a
 * világ-transzformáció nincs alkalmazva. Minden méret a nézet magasságához
 * arányos (u = height / 720), hogy 4 osztott nézetben is elférjen.
 */
import type { CameraView, HudModel } from '../core/types';
import { drawItemIcon } from '../render/itemRenderer';

const INK = '#10131c';
const PAPER = '#f4f1e8';
const ACCENT = '#ff5c4d';
const HIGHLIGHT = '#ffc857';
const TEAL = '#3ec6c0';
const GREEN = '#7ed957';
const MUTED = 'rgba(244, 241, 232, 0.62)';
const PANEL_FILL = 'rgba(16, 19, 28, 0.72)';
const PANEL_LINE = 'rgba(244, 241, 232, 0.22)';
const FONT_STACK = "'Trebuchet MS', 'Segoe UI', system-ui, sans-serif";
const TAU = Math.PI * 2;

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);
const smooth = (t: number): number => {
  const x = clamp(t, 0, 1);
  return x * x * (3 - 2 * x);
};

const font = (weight: number, size: number): string =>
  `${weight} ${Math.max(1, size).toFixed(1)}px ${FONT_STACK}`;

const roundRectPath = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void => {
  const rr = Math.min(r, Math.min(w, h) * 0.5);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
};

const panel = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  fill = PANEL_FILL,
  stroke: string | null = PANEL_LINE,
  lineWidth = 1,
): void => {
  roundRectPath(ctx, x, y, w, h, r);
  ctx.fillStyle = fill;
  ctx.fill();
  if (stroke) {
    ctx.lineWidth = Math.max(1, lineWidth);
    ctx.strokeStyle = stroke;
    ctx.stroke();
  }
};

/** vastag körvonal + kitöltés, hogy a szöveg minden háttéren olvasható legyen */
const outlinedText = (
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  fill: string,
  stroke: string,
  lineWidth: number,
): void => {
  ctx.lineJoin = 'round';
  ctx.lineWidth = Math.max(1, lineWidth);
  ctx.strokeStyle = stroke;
  ctx.strokeText(text, x, y);
  ctx.fillStyle = fill;
  ctx.fillText(text, x, y);
};

const formatClock = (t: number): string => {
  if (!Number.isFinite(t) || t < 0) return '–:––';
  const mins = Math.floor(t / 60);
  const secs = t - mins * 60;
  return `${mins}:${secs < 10 ? '0' : ''}${secs.toFixed(1)}`;
};

interface MinimapLayout {
  x: number;
  y: number;
  size: number;
}

/** Jobb felső minimap; visszaadja a helyét a többi panelhez. */
const drawMinimap = (
  ctx: CanvasRenderingContext2D,
  model: HudModel,
  playerCount: number,
  viewWidth: number,
  m: number,
  u: number,
): MinimapLayout => {
  const size = Math.max(46, (playerCount <= 1 ? 132 : 96) * u);
  const x = viewWidth - m - size;
  const y = m;

  ctx.save();
  roundRectPath(ctx, x, y, size, size, Math.max(6, 10 * u));
  ctx.fillStyle = 'rgba(16, 19, 28, 0.78)';
  ctx.fill();
  ctx.lineWidth = Math.max(1, 1.6 * u);
  ctx.strokeStyle = 'rgba(255, 200, 87, 0.5)';
  ctx.stroke();

  ctx.save();
  roundRectPath(ctx, x + 2.5 * u, y + 2.5 * u, size - 5 * u, size - 5 * u, Math.max(4, 8 * u));
  ctx.clip();

  const innerPad = 10 * u;
  const inner = size - innerPad;
  const drawX = x + innerPad / 2;
  const drawY = y + innerPad / 2;

  if (model.minimap && model.minimap.width > 0 && model.minimap.height > 0) {
    const scale = Math.min(inner / model.minimap.width, inner / model.minimap.height);
    const dw = model.minimap.width * scale;
    const dh = model.minimap.height * scale;
    const ox = x + (size - dw) / 2;
    const oy = y + (size - dh) / 2;
    ctx.globalAlpha = 0.95;
    ctx.drawImage(model.minimap, ox, oy, dw, dh);
    ctx.globalAlpha = 1;

    if (model.minimapDots && model.minimapDots.length) {
      for (const dot of model.minimapDots) {
        const dx = ox + dot.x * dw;
        const dy = oy + dot.y * dh;
        const r = (dot.isPlayer ? 4.6 : 3.1) * u;
        ctx.beginPath();
        ctx.arc(dx, dy, r + 1.2 * u, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(10,12,18,0.85)';
        ctx.fill();
        ctx.beginPath();
        ctx.arc(dx, dy, r, 0, Math.PI * 2);
        ctx.fillStyle = dot.isPlayer ? '#ffc857' : dot.color;
        ctx.fill();
        if (dot.isPlayer) {
          ctx.beginPath();
          ctx.arc(dx, dy, r + 2.6 * u, 0, Math.PI * 2);
          ctx.strokeStyle = 'rgba(244,241,232,0.9)';
          ctx.lineWidth = Math.max(1, 1.4 * u);
          ctx.stroke();
        }
      }
    }
  } else {
    // még nincs térkép: semleges, pálya-szerű placeholder
    ctx.fillStyle = 'rgba(244, 241, 232, 0.06)';
    ctx.fillRect(drawX, drawY, inner, inner);
    ctx.strokeStyle = 'rgba(244, 241, 232, 0.18)';
    ctx.setLineDash([3 * u, 3 * u]);
    ctx.lineWidth = Math.max(1, 1.4 * u);
    roundRectPath(ctx, x + 9 * u, y + 9 * u, size - 18 * u, size - 18 * u, 6 * u);
    ctx.stroke();
    ctx.setLineDash([]);
  }
  ctx.restore();

  // sarok-jelölés
  ctx.strokeStyle = 'rgba(255, 200, 87, 0.85)';
  ctx.lineWidth = Math.max(1, 2 * u);
  const c = Math.max(4, 7 * u);
  ctx.beginPath();
  ctx.moveTo(x + 3 * u, y + 3 * u + c);
  ctx.lineTo(x + 3 * u, y + 3 * u);
  ctx.lineTo(x + 3 * u + c, y + 3 * u);
  ctx.stroke();
  ctx.restore();

  return { x, y, size };
};

const drawBattlePanel = (
  ctx: CanvasRenderingContext2D,
  model: HudModel,
  m: number,
  u: number,
): number => {
  const b = model.battle;
  if (!b) return Math.max(34, 58 * u);
  const width = Math.max(112, 158 * u);
  const height = Math.max(40, 62 * u);

  panel(ctx, m, m, width, height, Math.max(6, 10 * u), 'rgba(16, 19, 28, 0.78)', b.teamColor, 1.6 * u);

  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.font = font(900, Math.max(8, 10.5 * u));
  ctx.fillStyle = b.teamColor;
  ctx.fillText(`${b.teamName} CSAPAT`, m + 9 * u, m + Math.max(12, 17 * u));

  // szívek
  const heartR = Math.max(3.5, 5 * u);
  for (let i = 0; i < 3; i++) {
    const cx = m + 12 * u + i * heartR * 3;
    const cy = m + height - Math.max(12, 17 * u);
    ctx.beginPath();
    ctx.arc(cx, cy, heartR, 0, Math.PI * 2);
    if (i < b.lives) {
      ctx.fillStyle = b.teamColor;
      ctx.fill();
    } else {
      ctx.strokeStyle = 'rgba(244, 241, 232, 0.3)';
      ctx.lineWidth = 1.4;
      ctx.stroke();
    }
  }

  ctx.textAlign = 'right';
  ctx.font = font(800, Math.max(7, 9 * u));
  ctx.fillStyle = b.out ? '#ff5c4d' : 'rgba(244, 241, 232, 0.75)';
  ctx.fillText(b.out ? 'KIESTÉL' : `${b.teamLives[0]} – ${b.teamLives[1]}`, m + width - 9 * u, m + height - Math.max(12, 17 * u));
  return height;
};

const drawPosition = (
  ctx: CanvasRenderingContext2D,
  model: HudModel,
  m: number,
  u: number,
): void => {
  const width = Math.max(72, 110 * u);
  const height = Math.max(34, 58 * u);
  const x = m;
  const y = m;
  const radius = Math.max(6, 10 * u);

  panel(ctx, x, y, width, height, radius, 'rgba(16, 19, 28, 0.76)', 'rgba(244, 241, 232, 0.2)', 1.4 * u);

  // akcentus csík a bal szélen
  roundRectPath(ctx, x, y, Math.max(3, 6 * u), height, radius);
  ctx.fillStyle = ACCENT;
  ctx.fill();

  const pos = Math.max(1, model.position);
  const total = Math.max(1, model.total);

  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.font = font(800, Math.max(7, 9.5 * u));
  ctx.fillStyle = MUTED;
  ctx.fillText('HELYEZÉS', x + 10 * u, y + Math.max(8, 12 * u));

  const numSize = Math.max(18, 34 * u);
  ctx.font = font(900, numSize);
  ctx.fillStyle = PAPER;
  ctx.fillText(String(pos), x + 10 * u, y + height - Math.max(3, 5 * u));
  const numWidth = ctx.measureText(String(pos)).width;

  ctx.font = font(800, Math.max(10, 17 * u));
  ctx.fillStyle = MUTED;
  ctx.fillText(`/${total}`, x + 10 * u + numWidth + 1.5 * u, y + height - Math.max(3, 5 * u));

  // drift-töltés (0..1; 0.85 és 1.9 a játékban, itt normalizálva)
  const barY = y + height + Math.max(3, 5 * u);
  const barH = Math.max(4, 8 * u);
  const barW = width;
  panel(ctx, x, barY, barW, barH, barH * 0.5, 'rgba(16, 19, 28, 0.66)', 'rgba(244, 241, 232, 0.18)', 1);

  const charge = clamp(model.driftCharge, 0, 1);
  if (charge > 0.005) {
    const color = charge >= 0.995 ? '#b48cff' : charge >= 0.447 ? HIGHLIGHT : TEAL;
    roundRectPath(ctx, x + u, barY + u, Math.max(2, (barW - 2 * u) * charge), barH - 2 * u, (barH - 2 * u) * 0.5);
    ctx.fillStyle = color;
    ctx.fill();
  }
  // mini-turbó küszöb jelölése (0.85 / 1.9 a fizikában)
  const notchX = x + u + (barW - 2 * u) * 0.447;
  ctx.fillStyle = 'rgba(244, 241, 232, 0.45)';
  ctx.fillRect(notchX - 0.75 * u, barY + 1.5 * u, Math.max(1, 1.5 * u), barH - 3 * u);
};

const drawLapPanel = (
  ctx: CanvasRenderingContext2D,
  model: HudModel,
  minimap: MinimapLayout,
  m: number,
  u: number,
): void => {
  const width = Math.max(84, 128 * u);
  const height = Math.max(42, 70 * u);
  const gap = Math.max(6, 10 * u);
  const x = minimap.x - gap - width;
  const y = m;

  panel(ctx, x, y, width, height, Math.max(6, 10 * u), 'rgba(16, 19, 28, 0.72)', 'rgba(244, 241, 232, 0.2)', 1.4 * u);

  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.font = font(800, Math.max(7, 9.5 * u));
  ctx.fillStyle = MUTED;
  ctx.fillText(model.battle ? 'CSATA' : 'KÖR', x + 8 * u, y + Math.max(8, 12 * u));

  if (model.battle) {
    ctx.font = font(900, Math.max(16, 26 * u));
    ctx.fillStyle = PAPER;
    ctx.fillText(formatClock(model.battle.timeLeft), x + 8 * u, y + Math.max(30, 45 * u));
    ctx.font = font(800, Math.max(7, 9 * u));
    ctx.fillStyle = 'rgba(244, 241, 232, 0.78)';
    ctx.textAlign = 'right';
    ctx.fillText(`${model.battle.teamLives[0]} – ${model.battle.teamLives[1]}`, x + width - 8 * u, y + Math.max(30, 45 * u));
    ctx.textAlign = 'left';
  } else {
    const lap = Math.max(0, model.lap);
    const laps = Math.max(1, model.laps);
    ctx.font = font(900, Math.max(16, 28 * u));
    ctx.fillStyle = lap >= laps ? HIGHLIGHT : PAPER;
    ctx.fillText(`${lap}/${laps}`, x + 8 * u, y + Math.max(30, 45 * u));
  }

  ctx.font = font(700, Math.max(7, 9 * u));
  ctx.fillStyle = MUTED;
  ctx.fillText(model.trackName, x + 8 * u, y + height - Math.max(4, 6 * u), width - 16 * u);

  // apró óra a kör alatt, csak ha van hely
  if (!model.battle && height >= 62 * u) {
    ctx.font = font(800, Math.max(7, 9 * u));
    ctx.fillStyle = 'rgba(244, 241, 232, 0.78)';
    ctx.textAlign = 'right';
    ctx.fillText(formatClock(model.raceTime), x + width - 8 * u, y + Math.max(30, 45 * u));
  }
};

const drawStatusChips = (
  ctx: CanvasRenderingContext2D,
  model: HudModel,
  x: number,
  y: number,
  u: number,
): number => {
  const chips: { text: string; color: string }[] = [];
  if (model.shield > 0) chips.push({ text: `PAJZS ${model.shield.toFixed(1)}`, color: TEAL });
  if (model.boost > 0) chips.push({ text: `TURBÓ ${model.boost.toFixed(1)}`, color: HIGHLIGHT });
  if (model.slow > 0) chips.push({ text: `LASSÚ ${model.slow.toFixed(1)}`, color: '#8ec5ff' });
  if (model.surface === 'boost') chips.push({ text: 'BOOST', color: HIGHLIGHT });
  if (model.surface === 'sidewalk') chips.push({ text: 'JÁRDA', color: '#efb14a' });
  else if (model.surface === 'kerb') chips.push({ text: 'SZEGÉLY', color: '#efb14a' });

  let cursorY = y;
  for (const chip of chips) {
    const fontSize = Math.max(7, 9.5 * u);
    ctx.font = font(800, fontSize);
    const width = ctx.measureText(chip.text).width + Math.max(10, 14 * u);
    const height = Math.max(12, 18 * u);
    panel(ctx, x, cursorY, width, height, height * 0.5, 'rgba(16, 19, 28, 0.74)', chip.color, 1.2 * u);
    ctx.fillStyle = chip.color;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(chip.text, x + Math.max(5, 7 * u), cursorY + height * 0.54);
    cursorY += height + Math.max(3, 4 * u);
  }
  return cursorY;
};

const drawStandings = (
  ctx: CanvasRenderingContext2D,
  model: HudModel,
  viewHeight: number,
  minimap: MinimapLayout,
  m: number,
  u: number,
): void => {
  const entries = model.standings;
  if (entries.length === 0) return;

  const width = Math.max(96, 160 * u);
  const x = minimap.x + minimap.size - width;
  const y = minimap.y + minimap.size + Math.max(6, 10 * u);
  const pad = Math.max(4, 6 * u);
  const headH = Math.max(11, 15 * u);
  const rowH = Math.max(12, 20 * u);
  const bottomLimit = viewHeight - Math.max(74, 122 * u) - m;
  const available = bottomLimit - y;
  const maxRows = Math.floor((available - headH - pad * 2) / rowH);
  if (maxRows < 2) return;

  const rows = Math.min(6, entries.length, maxRows);
  const height = headH + rows * rowH + pad * 2;
  panel(ctx, x, y, width, height, Math.max(6, 10 * u), 'rgba(16, 19, 28, 0.7)', 'rgba(244, 241, 232, 0.18)', 1.2 * u);

  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.font = font(800, Math.max(7, 9 * u));
  ctx.fillStyle = MUTED;
  ctx.fillText('ÁLLÁS', x + pad + 1 * u, y + pad + headH * 0.45);

  const nameSize = Math.max(7, 10 * u);
  const rowTop = y + pad + headH;
  for (let i = 0; i < rows; i++) {
    const entry = entries[i];
    const ry = rowTop + i * rowH;
    const cy = ry + rowH * 0.52;

    if (entry.isPlayer) {
      ctx.fillStyle = 'rgba(255, 92, 77, 0.2)';
      ctx.fillRect(x + 1, ry, width - 2, rowH);
      ctx.fillStyle = ACCENT;
      ctx.fillRect(x + 1, ry, Math.max(2, 3 * u), rowH);
    }

    ctx.textAlign = 'left';
    ctx.font = font(900, Math.max(8, 11 * u));
    ctx.fillStyle = entry.isPlayer ? PAPER : 'rgba(244, 241, 232, 0.88)';
    ctx.fillText(String(entry.position), x + pad + 2 * u, cy);

    const dotX = x + pad + Math.max(14, 20 * u);
    ctx.beginPath();
    ctx.arc(dotX, cy, Math.max(2, 3.2 * u), 0, TAU);
    ctx.fillStyle = entry.color;
    ctx.fill();
    ctx.lineWidth = Math.max(1, 1.2 * u);
    ctx.strokeStyle = 'rgba(16, 19, 28, 0.6)';
    ctx.stroke();

    ctx.font = font(entry.isPlayer ? 900 : 700, nameSize);
    ctx.fillStyle = entry.isPlayer ? PAPER : 'rgba(244, 241, 232, 0.82)';
    const nameX = dotX + Math.max(6, 8 * u);
    ctx.fillText(entry.name, nameX, cy, x + width - pad - nameX - (entry.finished ? 12 * u : 2 * u));

    if (entry.finished) {
      ctx.textAlign = 'right';
      ctx.font = font(900, Math.max(7, 9 * u));
      ctx.fillStyle = HIGHLIGHT;
      ctx.fillText('✓', x + width - pad, cy);
    }
  }
};

const drawBolt = (
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  color: string,
): void => {
  const s = Math.max(4, size);
  ctx.beginPath();
  ctx.moveTo(cx - s * 0.15, cy - s);
  ctx.lineTo(cx + s * 0.52, cy - s * 0.12);
  ctx.lineTo(cx + s * 0.08, cy - s * 0.08);
  ctx.lineTo(cx + s * 0.4, cy + s);
  ctx.lineTo(cx - s * 0.52, cy + s * 0.02);
  ctx.lineTo(cx - s * 0.02, cy - s * 0.02);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
};

const drawItemAndAbility = (
  ctx: CanvasRenderingContext2D,
  model: HudModel,
  viewWidth: number,
  viewHeight: number,
  m: number,
  u: number,
  time: number,
): void => {
  const slot = Math.max(40, 64 * u);
  const x = m;
  const y = viewHeight - m - slot;

  // tárgy-slot
  panel(ctx, x, y, slot, slot, Math.max(7, 12 * u), 'rgba(16, 19, 28, 0.8)', 'rgba(255, 200, 87, 0.5)', 1.6 * u);
  roundRectPath(ctx, x + 3 * u, y + 3 * u, slot - 6 * u, slot - 6 * u, Math.max(5, 9 * u));
  ctx.lineWidth = Math.max(1, 1.2 * u);
  ctx.strokeStyle = 'rgba(244, 241, 232, 0.12)';
  ctx.stroke();

  if (model.itemRolling) {
    const blink = Math.sin(time * 19) > -0.25;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = font(900, slot * 0.52);
    outlinedText(ctx, '?', x + slot / 2, y + slot * 0.46, blink ? HIGHLIGHT : 'rgba(244, 241, 232, 0.4)', INK, 3 * u);

    const trackW = slot - 10 * u;
    const p = clamp(model.itemProgress, 0, 1);
    const barY = y + slot - Math.max(5, 8 * u);
    ctx.fillStyle = 'rgba(244, 241, 232, 0.16)';
    ctx.fillRect(x + 5 * u, barY, trackW, Math.max(2, 3 * u));
    ctx.fillStyle = HIGHLIGHT;
    ctx.fillRect(x + 5 * u, barY, trackW * p, Math.max(2, 3 * u));
  } else if (model.item) {
    drawItemIcon(ctx, model.item.id, x + slot / 2, y + slot * 0.46, slot * 0.62);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.font = font(800, Math.max(6, 7.5 * u));
    ctx.fillStyle = 'rgba(244, 241, 232, 0.78)';
    ctx.fillText(model.item.name, x + slot / 2, y + slot - Math.max(3, 5 * u), slot - 8 * u);
  } else {
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = font(800, Math.max(8, 11 * u));
    ctx.fillStyle = 'rgba(244, 241, 232, 0.32)';
    ctx.fillText('–', x + slot / 2, y + slot / 2);
  }

  // képesség a tárgy fölött
  const radius = Math.max(12, 21 * u);
  const abCx = x + radius + Math.max(2, 3 * u);
  const abCy = y - Math.max(8, 14 * u) - radius;

  ctx.beginPath();
  ctx.arc(abCx, abCy, radius, 0, TAU);
  ctx.fillStyle = 'rgba(16, 19, 28, 0.82)';
  ctx.fill();
  ctx.lineWidth = Math.max(1.5, 2 * u);
  ctx.strokeStyle = 'rgba(244, 241, 232, 0.18)';
  ctx.stroke();

  const ratio = clamp(model.ability.ratio, 0, 1);
  if (ratio > 0.002) {
    ctx.beginPath();
    ctx.arc(abCx, abCy, radius, -Math.PI / 2, -Math.PI / 2 + TAU * ratio);
    ctx.lineCap = 'round';
    ctx.lineWidth = Math.max(2, 3.4 * u);
    ctx.strokeStyle = model.ability.ready ? GREEN : HIGHLIGHT;
    ctx.stroke();
    ctx.lineCap = 'butt';
  }

  drawBolt(ctx, abCx, abCy, radius * 0.5, model.ability.ready ? PAPER : 'rgba(244, 241, 232, 0.55)');

  if (viewWidth > 340) {
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.font = font(800, Math.max(7, 10 * u));
    ctx.fillStyle = model.ability.ready ? GREEN : MUTED;
    ctx.fillText(model.ability.name, abCx + radius + Math.max(5, 7 * u), abCy, Math.max(40, 100 * u));
  }
};

const drawSpeed = (
  ctx: CanvasRenderingContext2D,
  model: HudModel,
  viewWidth: number,
  viewHeight: number,
  m: number,
  u: number,
): void => {
  const radius = Math.max(30, 58 * u);
  const cx = viewWidth - m - radius;
  const cy = viewHeight - m - radius;
  const lineWidth = Math.max(3, 6.5 * u);
  const start = Math.PI * 0.78;
  const end = Math.PI * 2.22;
  const maxKmh = 180;

  ctx.beginPath();
  ctx.arc(cx, cy, radius + lineWidth * 0.85, 0, TAU);
  ctx.fillStyle = 'rgba(16, 19, 28, 0.74)';
  ctx.fill();
  ctx.lineWidth = Math.max(1, 1.2 * u);
  ctx.strokeStyle = 'rgba(244, 241, 232, 0.16)';
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(cx, cy, radius, start, end);
  ctx.lineCap = 'round';
  ctx.lineWidth = lineWidth;
  ctx.strokeStyle = 'rgba(244, 241, 232, 0.16)';
  ctx.stroke();

  const ratio = clamp(model.speedKmh / maxKmh, 0, 1);
  const warnColor =
    model.slow > 0
      ? '#8ec5ff'
      : model.surface === 'boost'
        ? '#ffc857'
        : model.surface === 'sidewalk' || model.surface === 'kerb'
          ? '#efb14a'
          : null;
  if (ratio > 0.004) {
    if (warnColor) {
      ctx.beginPath();
      ctx.arc(cx, cy, radius, start, start + (end - start) * ratio);
      ctx.lineWidth = lineWidth;
      ctx.strokeStyle = warnColor;
      ctx.stroke();
    } else {
      const gradient = ctx.createLinearGradient(cx - radius, cy + radius, cx + radius, cy - radius);
      gradient.addColorStop(0, TEAL);
      gradient.addColorStop(0.62, HIGHLIGHT);
      gradient.addColorStop(1, ACCENT);
      ctx.beginPath();
      ctx.arc(cx, cy, radius, start, start + (end - start) * ratio);
      ctx.lineWidth = lineWidth;
      ctx.strokeStyle = gradient;
      ctx.stroke();
    }
  }
  ctx.lineCap = 'butt';

  // skála osztások
  ctx.strokeStyle = 'rgba(244, 241, 232, 0.35)';
  ctx.lineWidth = Math.max(1, 1.2 * u);
  for (let i = 0; i <= 6; i++) {
    const a = start + ((end - start) * i) / 6;
    const inner = radius - lineWidth * 1.25;
    const outer = radius - lineWidth * 0.55;
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(a) * inner, cy + Math.sin(a) * inner);
    ctx.lineTo(cx + Math.cos(a) * outer, cy + Math.sin(a) * outer);
    ctx.stroke();
  }

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = font(900, Math.max(16, 29 * u));
  ctx.fillStyle = PAPER;
  ctx.fillText(String(Math.round(model.speedKmh)), cx, cy - 3 * u);

  ctx.font = font(800, Math.max(7, 9.5 * u));
  ctx.fillStyle = MUTED;
  ctx.fillText('km/h', cx, cy + Math.max(9, 15 * u));

  if (warnColor) {
    ctx.font = font(900, Math.max(8, 11 * u));
    ctx.fillStyle = warnColor;
    const label =
      model.slow > 0 ? 'LASSÚ' : model.surface === 'boost' ? 'BOOST' : model.surface === 'sidewalk' ? 'JÁRDA' : 'SZEGÉLY';
    ctx.fillText(label, cx, cy + Math.max(20, 28 * u));
  }
};

const drawCountdown = (
  ctx: CanvasRenderingContext2D,
  value: number,
  viewWidth: number,
  viewHeight: number,
  u: number,
  time: number,
): void => {
  const go = value <= 0;
  const label = go ? 'GO!' : String(Math.round(value));
  const radius = Math.max(34, (go ? 56 : 50) * u);
  const pulse = 1 + Math.sin(time * 9) * 0.045;

  ctx.save();
  ctx.translate(viewWidth / 2, viewHeight * 0.44);
  ctx.scale(pulse, pulse);

  ctx.beginPath();
  ctx.arc(0, 0, radius, 0, TAU);
  ctx.fillStyle = 'rgba(16, 19, 28, 0.64)';
  ctx.fill();
  ctx.lineWidth = Math.max(3, 5 * u);
  ctx.strokeStyle = go ? ACCENT : 'rgba(255, 200, 87, 0.85)';
  ctx.stroke();

  if (go) {
    ctx.beginPath();
    ctx.arc(0, 0, radius * 1.14, 0, TAU);
    ctx.lineWidth = Math.max(1.5, 2 * u);
    ctx.setLineDash([6 * u, 6 * u]);
    ctx.strokeStyle = 'rgba(255, 92, 77, 0.55)';
    ctx.stroke();
    ctx.setLineDash([]);
  }

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = font(900, Math.max(28, (go ? 52 : 62) * u));
  outlinedText(ctx, label, 0, 2 * u, go ? HIGHLIGHT : PAPER, 'rgba(16, 19, 28, 0.95)', Math.max(2, 6 * u));
  ctx.restore();
};

/** Fő HUD-rajzolás; a hívó már beállította a nézet kivágását. */
export function drawHud(
  ctx: CanvasRenderingContext2D,
  view: CameraView,
  model: HudModel,
  time: number,
): void {
  const u = Math.max(0.4, view.height / 720);
  const m = Math.max(6, 15 * u);

  let humans = 0;
  for (const entry of model.standings) {
    if (entry.isPlayer) humans++;
  }
  const playerCount = Math.max(1, humans);

  ctx.save();
  ctx.translate(view.x, view.y);
  ctx.beginPath();
  ctx.rect(0, 0, view.width, view.height);
  ctx.clip();

  const minimap = drawMinimap(ctx, model, playerCount, view.width, m, u);
  drawLapPanel(ctx, model, minimap, m, u);

  let topH = Math.max(34, 58 * u);
  if (model.battle) {
    topH = drawBattlePanel(ctx, model, m, u);
  } else if (model.practice) {
    // gyakorló módban nincs helyezés/álláslista, csak egy jelvény
    const w = Math.max(102, 150 * u);
    topH = Math.max(28, 42 * u);
    panel(ctx, m, m, w, topH, Math.max(6, 9 * u), 'rgba(16, 19, 28, 0.76)', TEAL, 1.4 * u);
    ctx.fillStyle = TEAL;
    ctx.font = font(900, Math.max(9, 11 * u));
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText('GYAKORLÁS', m + Math.max(9, 12 * u), m + topH * 0.54);
  } else {
    drawPosition(ctx, model, m, u);
    if (view.height >= 360) drawStandings(ctx, model, view.height, minimap, m, u);
  }

  // állapot-chipek a helyezés/jelvény alatt
  const chipY = m + topH + Math.max(3, 5 * u) + Math.max(4, 8 * u) + Math.max(5, 7 * u);
  drawStatusChips(ctx, model, m, chipY, u);

  drawItemAndAbility(ctx, model, view.width, view.height, m, u, time);
  drawSpeed(ctx, model, view.width, view.height, m, u);

  if (model.countdown !== null) {
    drawCountdown(ctx, model.countdown, view.width, view.height, u, time);
  }

  ctx.restore();

  if (model.countdown === null && model.message) {
    drawBanner(ctx, view, model.message, 0.5);
  }
}

/** Rövid, nagy üzenet a nézet közepén. progress 0..1 (0 és 1 = eltűnik). */
export function drawBanner(
  ctx: CanvasRenderingContext2D,
  view: CameraView,
  text: string,
  progress: number,
): void {
  if (!text) return;
  const t = clamp(progress, 0, 1);
  const appear = smooth(t / 0.15);
  const disappear = smooth((t - 0.82) / 0.18);
  const alpha = appear * (1 - disappear);
  if (alpha <= 0.001) return;

  const u = Math.max(0.4, view.height / 720);
  const width = view.width;
  const height = view.height;

  ctx.save();
  ctx.translate(view.x, view.y);
  ctx.beginPath();
  ctx.rect(0, 0, width, height);
  ctx.clip();
  ctx.globalAlpha = alpha;

  let size = Math.max(14, 30 * u);
  ctx.font = font(900, size);
  const maxWidth = width * 0.7;
  const measured = ctx.measureText(text).width;
  if (measured > maxWidth) {
    size = Math.max(10, size * (maxWidth / measured));
    ctx.font = font(900, size);
  }
  const textWidth = ctx.measureText(text).width;

  const padX = Math.max(10, 20 * u);
  const padY = Math.max(5, 9 * u);
  const boxW = Math.min(width * 0.82, textWidth + padX * 2);
  const boxH = size * 1.25 + padY * 2;

  const slide = (1 - appear) * 36 * u - disappear * 36 * u;
  // a felső sávban, hogy soha ne takarja a játékost
  ctx.translate(width / 2 + slide, height * 0.14);

  panel(
    ctx,
    -boxW / 2,
    -boxH / 2,
    boxW,
    boxH,
    Math.max(6, 12 * u),
    'rgba(16, 19, 28, 0.82)',
    'rgba(255, 200, 87, 0.55)',
    1.6 * u,
  );

  roundRectPath(ctx, -boxW / 2, -boxH / 2, Math.max(3, 6 * u), boxH, Math.max(4, 8 * u));
  ctx.fillStyle = ACCENT;
  ctx.fill();

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  outlinedText(ctx, text, 0, 1 * u, PAPER, 'rgba(16, 19, 28, 0.95)', Math.max(2, 5 * u));

  ctx.restore();
}
